import { MarketListing } from '@components/arsenal/market/MarketListing';
import { MarketCredits } from '@components/arsenal/market/MarketCredits';
import { ArsenalHeader } from '@components/arsenal/ArsenalHeader';
import { Select } from '@components/ui/Select';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

export function Market() {
  const [category, setCategory] = useState('');

  const { t } = useTranslation(undefined, { keyPrefix: 'Market' });
  const { userData } = useAppStore();

  const categories = [
    { value: '', label: t('all') },

    { value: 'chassis', label: t('chassis') },
    { value: 'weapons', label: t('weapons') },
    { value: 'fishing', label: t('fishing') },
    { value: 'mining', label: t('mining') },
  ];

  const availableChassis = useMemo(() => {
    if (category && category !== 'chassis') return [];
    const ownedChassis = userData.chassis.map(c => c.key);
    const ownedBlueprints = userData.blueprints
      .filter((blueprint) => blueprint.type === 'CHASSIS')
      .map((blueprint) => blueprint.key);
    return Shared.chassisData
      .filter((chassis) => !ownedChassis.includes(chassis.key))
      .filter((chassis) => !ownedBlueprints.includes(chassis.key));
  }, [category, userData]);

  const availableWeapons = useMemo(() => {
    if (category && category !== 'weapons') return [];
    const ownedWeapons = userData.weapons.map(w => w.key);
    const ownedBlueprints = userData.blueprints
      .filter((blueprint) => blueprint.type === 'WEAPON')
      .map((blueprint) => blueprint.key);
    return Shared.weaponsData
      .filter((weapon) => !ownedWeapons.includes(weapon.key))
      .filter((weapon) => !ownedBlueprints.includes(weapon.key));
  }, [category, userData]);

  const availableItems = useMemo(() => {
    if (category && !['fishing', 'mining'].includes(category)) return [];

    const canBuyItem = (item: Shared.ItemData) => {
      if (item.crafting?.infinite && !userData.blueprints.some((b) => b.key === item.key)) {
        return true;
      }

      return !userData.items.some((i) => i.key === item.key)
        && !userData.blueprints.some((b) => b.key === item.key);
    };

    return Shared.itemData
      .filter((item) => !!item.crafting)
      .filter((item) => !category || item.key.startsWith(category))
      .filter((item) => canBuyItem(item));
  }, [category, userData]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ArsenalHeader description={t('description')} title={t('title')} bg="market.avif" />
      <MarketCredits />
      <Select onChange={setCategory} options={categories} value={category} />

      <div className="flex-1 overflow-y-auto bg-black">
        {availableChassis.map((chassis) => (
          <MarketListing value={chassis.key} key={chassis.key} type="CHASSIS" />
        ))}
        {availableWeapons.map((weapon) => (
          <MarketListing value={weapon.key} key={weapon.key} type="WEAPON" />
        ))}
        {availableItems.map((item) => (
          <MarketListing value={item.key} key={item.key} type="ITEM" />
        ))}
      </div>
    </div>
  );
}